import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import Cards from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";
import { fetchBundles, createCheckoutSession, payBundle, updateBundlePayment } from "./service";
import Loading from "../Loading/Loading";
import { toast } from "react-toastify";
import { getUserDetails } from "../../Redux/UserDetailsSlice/UserDetailsSlice";
import Translate from "../Multilanguage/Translate";
import { getAndroidSubscriptionDetails } from "../../Screens/Subscription/service";

function Bundles({
  tokenId = null,
  onBundleSelect = null,
  hideTransactionHistory = false,
  episodePrice = null,
  onClose = null,
  isPopupMode = false,
}) {
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const sessionId = useSelector((state) => state?.sessionId?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const user = useSelector((state) => state?.user?.value);
  const userDetails = useSelector((state) => state?.userDetails?.value);
  const urlParams = new URLSearchParams(window.location.search);
  const androidToken = urlParams.get("antkn");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userCoins = useSelector(
    (state) => state?.userDetails?.value?.user_coins,
  );
  const coinBalances = userCoins
    ? [
        {
          token_id: userCoins?.token_id || "coins",
          token_name: userCoins?.token_name || "Coins",
          token_symbol: userCoins?.token_symbol || "",
          current_balance: userCoins?.current_balance || 0,
        },
      ]
    : [];
  const selectedBalance =
    coinBalances.find(
      (balance) => String(balance?.token_id) === String(tokenId),
    ) ||
    coinBalances[0] ||
    null;

  const [bundles, setBundles] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscription_terms, setSubscriptionTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBundleId, setSelectedBundleId] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [consentModal, setConsentModal] = useState(null); // { savedCard, bundleInfo, bundleId }
  const [payLoading, setPayLoading] = useState(false);

  const appInfo = {
    projectDetails: projectInfo,
    accessToken: accessToken,
    sessionId,
  };

  useEffect(() => {
    loadPageData();
  }, [tokenId]);

    useEffect(() => {
    if (androidToken) {
      const deviceId = urlParams.get("deviceId");
      const info = {
        projectConfig: projectInfo?.projectConfig,
        geoInfo: projectInfo?.geoInfo,
        device_id: deviceId,
      };
      fetchAndroidSubscriptionDetails(androidToken, info);
    }
  }, [androidToken]);
  
  const fetchAndroidSubscriptionDetails = async (androidToken, info) => {

        const data = {
            projectDetails: info,
            accessToken: accessToken,
        };
        const response = await getAndroidSubscriptionDetails(
            data,
            androidToken
        );

        if (response?.status === 200) {
            localStorage.setItem("userId", response?.data?.data[0]?.user_id);
            localStorage.setItem("isAndroid", true);
            localStorage.setItem("deviceType", "android-web");
            localStorage.setItem('selectedLanguageCode', response?.data?.data[0]?.language?.short_code);
            localStorage.setItem('selectedLanguageId', response?.data?.data[0]?.language?.language_id);
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);

            // Remove the 'antkn & deviceId' parameters
            params.delete('antkn');
            params.delete('deviceId');

            const newUrl = `${url.pathname}?${params.toString()}`;
            window.location.href = newUrl;
        } else {
            localStorage.removeItem("deviceType")
            localStorage.removeItem("isAndroid")
            navigate('/home');
        }
    };

  
  useEffect(() => {
    setSelectedBundleId(null);
    setCheckoutLoading(false);
  }, [tokenId]);

  const loadPageData = async () => {
    setLoading(true);
    setError(null);

    try {
      const bundleResult = await fetchBundles(appInfo, tokenId);

      if (bundleResult.success) {
        const payload = bundleResult.data?.data || {};
        setBundles(payload?.bundles || []);
        setSubscriptions(payload?.subscriptions || []);
        setSubscriptionTerms(payload?.subscription_terms || []);
      } else {
        setError(bundleResult.error || "Failed to load bundles");
        setSubscriptions([]);
      }
    } catch (error) {
      setError("Failed to load bundles");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionClick = (plan) => {
    navigate(`/checkout/${plan?.subscription_id}`, {
      state: {
        subscriptionId: plan?.subscription_id,
        subscriptionPrice: plan?.price,
      },
    });
  };

  const handleBundleClick = async (bundle) => {
    setSelectedBundleId(bundle?.bundle_id || null);
  };

  const selectedBundle =
    bundles.find((bundle) => bundle?.bundle_id === selectedBundleId) || null;

  const updateUserCoinsFromPaymentResponse = (paymentResponse) => {
    const responseData = paymentResponse?.data || {};
    const newBalance = Number(responseData?.new_balance);
    if (!Number.isFinite(newBalance)) {
      return;
    }

    const tokenId = Number(responseData?.token_id);
    const existingUserCoins =
      typeof userDetails?.user_coins === "object" &&
      userDetails?.user_coins !== null
        ? userDetails.user_coins
        : {};

    const updatedUserCoins = {
      ...existingUserCoins,
      token_id: existingUserCoins?.token_id,
      current_balance: newBalance,
    };

    dispatch(
      getUserDetails({
        userDetails: {
          ...(typeof userDetails === "object" && userDetails !== null
            ? userDetails
            : {}),
          user_coins: updatedUserCoins,
        },
      }),
    );
  };

  const handleCloseClick = () => {
    if (typeof onClose === "function") {
      onClose();
    }
    localStorage.removeItem("vertical_show_redirect");
  };

  const handlePostPaymentRedirect = () => {
    const verticalShowRedirect = localStorage.getItem("vertical_show_redirect");
    if (!verticalShowRedirect) {
      return false;
    }

    try {
      const redirectData = JSON.parse(verticalShowRedirect);
      localStorage.removeItem("vertical_show_redirect");

      if (redirectData?.vanityUrl) {
        if (typeof onClose === "function") {
          onClose();
        }

        navigate(redirectData.vanityUrl, {
          replace: true,
          state: {
            resumeFromBundlePurchase: true,
            pendingConsumptionType: redirectData?.consumptionType,
            pendingVideoId: redirectData?.videoId,
            pendingShowId: redirectData?.showId,
          },
        });
        return true;
      }
    } catch (error) {
      localStorage.removeItem("vertical_show_redirect");
    }

    return false;
  };

  const androidRedirect = (status) => {
    localStorage.removeItem("deviceType")
    localStorage.removeItem("isAndroid")
    const redirectUrl = status === "success"
      ? appInfo?.projectDetails?.projectConfig.config?.ANDROID_PAYMENT_SUCCESS_URL
      : appInfo?.projectDetails?.projectConfig.config?.ANDROID_PAYMENT_FAIL_URL;
    if (redirectUrl) {
      window.location.href = redirectUrl + "?bundle=true";
    }
  }

  const handleContinueClick = async () => {
    if (!user) {
      navigate(
        projectInfo?.projectConfig?.config?.WEBSITE_NEW_CHECKOUT_REQUIRED ==
          "true"
          ? "/checkout"
          : "/login",
        {
          state: {
            path: location?.pathname,
            prevPath: location?.state?.prevPath,
            activeLogin: true,
            from: "VerticalShowPlayer",
          },
        },
      );
      return;
    }
    if (!selectedBundle || checkoutLoading) {
      return;
    }

    try {
      setCheckoutLoading(true);

      const result = await createCheckoutSession(
        appInfo,
        selectedBundle.bundle_id,
      );

      if (!result.success) {
        toast.error(result.error || "Failed to initiate checkout. Please try again.", {
          position: "top-right",
        });
        setCheckoutLoading(false);
        return;
      }

      const responseData = result.data;

      if (responseData?.requires_consent) {
        // Saved card flow — show consent modal
        setConsentModal({
          savedCard: responseData.saved_card,
          bundleInfo: responseData.bundle_info,
          bundleId: selectedBundle.bundle_id,
        });
        setCheckoutLoading(false);
        return;
      }

      // Direct checkout session
      const url = responseData?.url || responseData?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Failed to initiate checkout. Please try again.", {
          position: "top-right",
        });
        setCheckoutLoading(false);
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error?.response?.data?.message || "An error occurred. Please try again.", {
        position: "top-right",
      });
      setCheckoutLoading(false);
    }
  };

  const handleConsentPay = async (paymentChoice) => {
    if (!consentModal || payLoading) return;
    setPayLoading(true);
    const isAndroid = localStorage.getItem("isAndroid");

    try {
      const result = await payBundle(
        appInfo,
        consentModal.bundleId,
        paymentChoice,
      );

      if (!result.success) {
        toast.error(result.error || "Payment failed. Please try again.", {
          position: "top-right",
        });
        if (isAndroid == "true") {
          androidRedirect("fail");
        }
        setPayLoading(false);
        return;
      }

      const responseData = result.data;

      // 3DS required
      if (
        responseData?.requires_action ||
        responseData?.data?.requires_action
      ) {
        const { client_secret, publishable_key } =
          responseData?.data || responseData;
        const stripe = await loadStripe(
          publishable_key,
        );
        const { error, paymentIntent } = await stripe.handleNextAction({
          clientSecret: client_secret,
        });
        if (error) {
          toast.error(error.message, {
            position: "top-right",
          });
          setPayLoading(false);
          return;
        }

        if (paymentIntent?.status === "succeeded") {
          const paymentResponse = await updateBundlePayment(
            appInfo,
            consentModal.bundleId,
            "stripe",
            paymentIntent.id,
            consentModal.bundleInfo?.amount,
            consentModal.bundleInfo?.currency,
          ); 
          
          updateUserCoinsFromPaymentResponse(paymentResponse?.data);
          setConsentModal(null);
          toast.success("Payment successful! Coins have been credited.", {
            position: "top-right",
          });
          if (isAndroid == "true") {
            androidRedirect("success");
          }

          if (!handlePostPaymentRedirect()) {
            navigate(-1);
          }
          return;
        }

        toast.error("Payment requires further action.", {
          position: "top-right",
        });
        if (isAndroid == "true") {
          androidRedirect("fail");
        }
        setPayLoading(false);
        return;
      }

      // Checkout redirect
      const url = responseData?.data?.url || responseData?.url;
      if (url) {
        setConsentModal(null);
        window.location.href = url;
        return;
      }

      // Saved card success
      if (responseData?.success) {
        updateUserCoinsFromPaymentResponse(responseData);
        setConsentModal(null);

        toast.success("Payment successful! Coins have been credited.", {
          position: "top-right",
        });

        if (!handlePostPaymentRedirect()) {
          navigate(-1);
        }
        if (isAndroid == "true") {
          androidRedirect("success");
        }
      } else {
        toast.error("Payment failed. Please try again.", {
          position: "top-right",
        });
        setPayLoading(false);
      }
    } catch (error) {
      console.error("Pay error:", error);
      toast.error(error?.response?.data?.message || "An error occurred. Please try again.", {
        position: "top-right",
      });
      if (isAndroid == "true") {
        androidRedirect("fail");
      }
      setPayLoading(false);
    }
  };

  if (loading) {
    if (isPopupMode) {
      return (
        <div className="bundles-container popup-mode">
          <div className="coins-unified-section">
            <div className="bundles-inline-loader">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      );
    }

    return <Loading />;
  }

  if (error) {
    return (
      <div className="bundles-container">
        <div className="bundles-error">
          <p>{error}</p>
          <button onClick={loadPageData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {consentModal && (
        <div
          className="consent-modal-overlay"
          onClick={() => !payLoading && setConsentModal(null)}
        >
          <div
            className="consent-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="consent-modal-header">
              <h3>
                <Translate textKey={'confirm_purchase'} />
              </h3>
              <button
                className="close-icon-btn"
                onClick={() => !payLoading && setConsentModal(null)}
                disabled={payLoading}
              >
                ✕
              </button>
            </div>

            {/* <div className="consent-bundle-info">
            <p className="consent-bundle-name">{consentModal.bundleInfo?.bundle_name}</p>
            <p className="consent-bundle-tokens">
              {consentModal.bundleInfo?.token_quantity?.toLocaleString()} {consentModal.bundleInfo?.token_name}
            </p>
            <p className="consent-bundle-amount">
              {consentModal.bundleInfo?.currency}{consentModal.bundleInfo?.amount}
            </p>
          </div> */}

            <div className="consent-card-section">
              <p className="consent-card-label"><Translate textKey={'saved_payment_method'} /></p>
              <Cards
                expiry={
                  String(consentModal.savedCard?.exp_month).padStart(2, "0") +
                  String(consentModal.savedCard?.exp_year).slice(-2)
                }
                focused={"number"}
                name={""}
                number={"**** **** **** " + consentModal.savedCard?.last4}
                preview={true}
                issuer={consentModal.savedCard?.brand}
              />
            </div>

            <div className="consent-actions">
              <button
                className="consent-pay-btn"
                onClick={() => handleConsentPay("saved_card")}
                disabled={payLoading}
              >
                {payLoading ? <Translate textKey={'processing'} /> : <Translate textKey={'pay_using_saved_card'} />}
              </button>
              <button
                className="consent-other-method-btn"
                onClick={() => handleConsentPay("checkout")}
                disabled={payLoading}
              >
                <Translate textKey={'use_another_payment_method'} />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`bundles-container ${isPopupMode ? "popup-mode" : ""}`}>
        <div className="coins-unified-section">
          {isPopupMode ? (
            <div className="popup-price-balance-header">
              <div className="popup-price-balance-left">
                <div className="price-display">
                  <span><Translate textKey={'price'} />:</span>
                  <div className="price-value">
                    {selectedBalance?.token_symbol ? (
                      <img
                        src={selectedBalance.token_symbol}
                        alt={selectedBalance?.token_name || "Token"}
                        width="18"
                        height="18"
                      />
                    ) : null}
                    <span>{episodePrice || 0}</span>
                  </div>
                </div>

                <span className="popup-header-divider">|</span>

                <div className="price-display">
                  <span><Translate textKey={'balance'} />:</span>
                  <div className="price-value">
                    {selectedBalance?.token_symbol ? (
                      <img
                        src={selectedBalance.token_symbol}
                        alt={selectedBalance?.token_name || "Token"}
                        width="18"
                        height="18"
                      />
                    ) : null}
                    <span>{selectedBalance?.current_balance || 0}</span>
                  </div>
                </div>
              </div>

              {onClose && (
                <button
                  className="close-icon-btn"
                  onClick={handleCloseClick}
                  title="Close"
                >
                  ✕
                </button>
              )}
            </div>
          ) : (
            (episodePrice !== null || onClose) && (
              <div className="episode-price-close-header">
                {episodePrice !== null ? (
                  <div className="price-display">
                    <span><Translate textKey={'price'} />:</span>
                    <div className="price-value">
                      {selectedBalance?.token_symbol ? (
                        <img
                          src={selectedBalance.token_symbol}
                          alt={selectedBalance?.token_name || "Token"}
                          width="18"
                          height="18"
                        />
                      ) : null}
                      <span>{episodePrice}</span>
                    </div>
                  </div>
                ) : null}
                {onClose && (
                  <button
                    className="close-icon-btn"
                    onClick={handleCloseClick}
                    title="Close"
                  >
                    ✕
                  </button>
                )}
              </div>
            )
          )}

          {/* Coin Balance Header */}
          {!isPopupMode && (
            <div className="coin-balance-header">
              <div className="balance-info">
                {coinBalances.map((balanceItem) => (
                  <div
                    className="balance-item"
                    key={balanceItem?.token_id || balanceItem?.token_name}
                  >
                    <span>{balanceItem?.token_name || "Token"}:</span>
                    <div className="balance-value">
                      {balanceItem?.token_symbol ? (
                        <img
                          src={balanceItem.token_symbol}
                          alt={balanceItem?.token_name || "Token"}
                          width="22"
                          height="22"
                        />
                      ) : null}
                      <span>{balanceItem?.current_balance || 0}</span>
                    </div>
                  </div>
                ))}
              </div>

              {!hideTransactionHistory && (
                <div
                  className="transaction-history"
                  onClick={() => {
                    //go to /coin/transaction
                    navigate("/account/billing-activity");
                  }}
                >
                  <Translate textKey={'transaction_details'} /> <span className="arrow">›</span>
                </div>
              )}
            </div>
          )}

          {/* Divider Line */}
          <div className="section-divider"></div>

          {!!subscriptions.length && (
            <div className="vip-section">
              <div className="vip-header">
                <h3><Translate textKey={'subscribe'} /></h3>
              </div>

              <div className="vip-plans">
                {subscriptions.map((plan) => (
                  <div
                    key={
                      plan?.subscription_id ||
                      plan?.vanity_url ||
                      plan?.subscription_name
                    }
                    className="vip-card"
                    onClick={() => handleSubscriptionClick(plan)}
                  >
                    <div className="vip-image-container">
                      <img
                        src={plan?.subscription_symbol}
                        alt={plan?.subscription_name || "Subscription"}
                        className="vip-image"
                      />
                    </div>
                    <div className="vip-card-upper">
                      <div className="vip-plan-name">
                        {plan?.subscription_name}
                      </div>
                      <div className="vip-price">{plan?.subscription_text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Coins Section */}
          <div className="coins-content">
            <h2 className="bundles-title"><Translate textKey={'top_up'} /></h2>

            <div className="bundles-grid">
              {bundles.map((bundle) => {
                return (
                  <div
                    key={bundle.bundle_id}
                    className={`bundle-card ${selectedBundleId === bundle.bundle_id ? "selected" : ""}`}
                    onClick={() => handleBundleClick(bundle)}
                  >
                    {bundle?.offer_text && (
                      <div className="bundle-discount-badge-container">
                      <div className="bundle-discount-badge">
                        {bundle.offer_text}
                      </div>
                      </div>
                    )}

                    <div className="bundle-header">
                      {bundle?.token_symbol ? (
                        <img
                          src={bundle.token_symbol}
                          alt={bundle?.token_name || "Token"}
                          width="22"
                          height="22"
                        />
                      ) : null}
                      <div className="bundle-quantity">
                        {bundle?.bundle_quantity_text}
                      </div>
                      <div className="bundle-bonus-quantity">
                        {bundle?.bundle_bonus_text}
                      </div>
                    </div>

                    <div className="bundle-price">
                      {bundle.price_text}
                    </div>
                  </div>
                );
              })}
            </div>

            {bundles.length > 0 && (
              <div className="bundle-selection-actions">
                <button
                  className="bundle-continue-button"
                  onClick={handleContinueClick}
                  disabled={!selectedBundle || checkoutLoading}
                  aria-busy={checkoutLoading}
                >
                  {checkoutLoading && (
                    <span className="button-spinner" aria-hidden="true"></span>
                  )}
                  <span><Translate textKey={'continue'} /></span>
                </button>
              </div>
            )}

            {bundles.length === 0 && (
              <div className="no-bundles">
                <p><Translate textKey={'no_bundles_available'} /></p>
              </div>
            )}
          </div>
          {subscription_terms.length > 0 && (
            <div className="bundles-disclaimer">
              {
                subscription_terms.map((term, index) => (
                  <p key={index} className="subscription-term">{term}</p>
                ))
              }
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Bundles;
